import React from "react";
import { Card, CardHeader, CardTitle } from "./ui/card";
import { CircleAlert, Clock, CircleX, CircleCheckBig } from "lucide-react";
import LogoBlack from "../assets/logo-black.png";

export type SigningLinkVariant =
  | "expired"
  | "invalid"
  | "missing"
  | "alreadySigned"
  | "unavailable";

interface SigningLinkStatusProps {
  variant: SigningLinkVariant;
  originatorCompanyName?: string;
}

/** Display name or generic phrase for \"the organisation that sent you…\" */
function contactOrg(originatorCompanyName?: string): string {
  const n = originatorCompanyName?.trim();
  if (n) {
    return n;
  }
  return "the organisation that sent you this link";
}

const SigningLinkStatus: React.FC<SigningLinkStatusProps> = ({
  variant,
  originatorCompanyName,
}) => {
  const org = contactOrg(originatorCompanyName);

  const contactResend = (
    <>
      <strong className="text-yavrio-charcoal">
        Please contact {org}
        {originatorCompanyName?.trim()
          ? " so they can resend your signing email."
          : " so they can send a new mandate email."}
      </strong>
    </>
  );

  const config: Record<
    SigningLinkVariant,
    { Icon: typeof CircleAlert; title: string; body: React.ReactNode }
  > = {
    expired: {
      Icon: Clock,
      title: "This signing link has expired",
      body: (
        <>
          <p className="mb-4">
            For your security, signing links expire after 24 hours.{" "}
            {contactResend}
          </p>
        </>
      ),
    },
    invalid: {
      Icon: CircleX,
      title: "This link is not valid",
      body: (
        <>
          <p className="mb-4">
            The link may be incomplete, outdated, or may have already been
            replaced. {contactResend} You can also try again later from the
            original email when you have been sent an updated link.
          </p>
        </>
      ),
    },
    missing: {
      Icon: CircleAlert,
      title: "Signing link incomplete",
      body: (
        <p className="mb-4">
          Open the mandate signing link provided in your email. If your message
          is missing a button or link,{" "}
          <strong className="text-yavrio-charcoal">please contact {org}.</strong>
        </p>
      ),
    },
    alreadySigned: {
      Icon: CircleCheckBig,
      title: "This mandate has already been signed",
      body: (
        <>
          <p className="mb-4">
            Nothing more is required from this signing link at this time.
            {originatorCompanyName?.trim()
              ? ` This mandate relates to ${originatorCompanyName.trim()}. `
              : " "}
            If something still looks incorrect,{" "}
            <strong className="text-yavrio-charcoal">
              please contact {org}.
            </strong>
          </p>
        </>
      ),
    },
    unavailable: {
      Icon: CircleAlert,
      title: "We could not verify this signing link",
      body: (
        <p className="mb-4">
          Something went wrong on our side, or your connection interrupted the
          request. Please try again in a moment. If the problem persists,{" "}
          <strong className="text-yavrio-charcoal">please contact {org}.</strong>
        </p>
      ),
    },
  };

  const { Icon, title, body } = config[variant];

  const iconWrapClass =
    variant === "expired"
      ? "text-amber-600"
      : variant === "alreadySigned"
      ? "text-gray-500"
      : "text-red-600";

  return (
    <Card className="flex flex-col w-full mx-auto p-10 max-w-7xl transition-all duration-500">
      <CardHeader className="flex flex-col items-center gap-8">
        <Icon className={`w-16 h-16 mx-auto ${iconWrapClass}`} />
        <CardTitle className="text-4xl font-bold text-center text-yavrio-charcoal">
          {title}
        </CardTitle>
        <div className="flex flex-col gap-4 text-center text-lg max-w-2xl">
          {body}
          <p className="mt-4 text-gray-700">
            Need help? Email{" "}
            <a href="mailto:support@yavr.io" className="text-blue-500">
              support@yavr.io
            </a>
            .
          </p>
          <img
            src={LogoBlack}
            alt="yavrio logo"
            className="w-1/4 mx-auto mt-12"
            style={{ objectFit: "contain", objectPosition: "center" }}
          />
        </div>
      </CardHeader>
    </Card>
  );
};

export default SigningLinkStatus;
